/**
 * Carbon Strategy Page — /housing/carbon-strategy
 * Single source of truth: all pathway data from journey.carbon_pathway (backend).
 * computeEligibility() removed — was the root cause of PoA discrepancy across pages.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, CheckCircle2, XCircle, AlertCircle, ArrowRight,
         GitBranch, Layers, DollarSign, ChevronDown, ChevronUp } from "lucide-react";
import { useHousingJourney } from "@/hooks/useHousingJourney";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Canonical status config — covers all backend-returned statuses ─────────
const STATUS_CONFIG = {
  recommended:  { label: "High",         color: "bg-emerald-100 text-emerald-700 border-emerald-300",  dot: "bg-emerald-500" },
  high:         { label: "High",         color: "bg-emerald-100 text-emerald-700 border-emerald-300",  dot: "bg-emerald-500" },
  conditional:  { label: "Conditional",  color: "bg-amber-100   text-amber-700   border-amber-300",   dot: "bg-amber-500"   },
  viable:       { label: "Medium",       color: "bg-amber-100   text-amber-700   border-amber-300",   dot: "bg-amber-400"   },
  below_scale:  { label: "Low",          color: "bg-slate-100   text-slate-500   border-slate-300",    dot: "bg-slate-300"   },
  low:          { label: "Low",          color: "bg-slate-100   text-slate-500   border-slate-300",    dot: "bg-slate-300"   },
  not_eligible: { label: "Not Eligible", color: "bg-red-50      text-red-600     border-red-200",      dot: "bg-red-400"     },
  early_stage:  { label: "Early Stage",  color: "bg-slate-100   text-slate-500   border-slate-300",    dot: "bg-slate-300"   },
};

function PathwayPill({ name, status, onClick, active }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.low;
  return (
    <button onClick={onClick} data-testid={`pathway-pill-${name.toLowerCase().replace(/\s+/g,"-")}`}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all ${
        active ? "bg-[#0F172A] text-white border-slate-700 shadow-sm" : `${cfg.color} hover:opacity-80`
      }`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? "bg-teal-400" : cfg.dot}`} />
      <span>{name}</span>
      {!active && <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full border ml-1 ${cfg.color}`}>{cfg.label}</span>}
    </button>
  );
}

// ── Pathway detail block driven entirely from backend data ────────────────
function PathwayDetailBlock({ pathway, journey }) {
  const [reqOpen, setReqOpen] = useState(false);
  if (!pathway) return null;

  const demo = journey?.demo_unit || {};
  const prog = journey?.programme || {};

  return (
    <div className="space-y-4">
      {/* Backend-driven reasoning */}
      <SectionCard>

        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Pathway Assessment</p>
        <p className="text-xs text-slate-600 leading-relaxed mb-4">{pathway.reasoning}</p>

        {/* Requirements checklist */}
        {pathway.requirements?.length > 0 && (
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <button className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left"
              onClick={() => setReqOpen(v => !v)}>
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                Requirements ({pathway.requirements.filter(r => r.met).length}/{pathway.requirements.length} met)
              </p>
              {reqOpen ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
            </button>
            {reqOpen && (
              <div className="px-4 py-3 space-y-1.5">
                {pathway.requirements.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    {r.met
                      ? <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0" />
                      : <XCircle     size={11} className="text-slate-300 flex-shrink-0" />}
                    <span className={r.met ? "text-slate-700" : "text-slate-400"}>{r.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      
      </SectionCard>

      {/* Key metrics for this pathway */}
      {(pathway.status === "recommended" || pathway.status === "high") && (
        <div className="grid grid-cols-3 gap-4">
          {[
            ["Embodied reduction", `${demo.embodied_reduction_tco2e} tCO₂e/unit`, "One-time, at construction"],
            ["Programme total", `${(prog.total_embodied_tco2e||0).toLocaleString()} tCO₂e`, "Across 1,000 units"],
            ["No credit sales", "Supply-chain claim", "Buyer-supported decarbonization"],
          ].map(([l, v, s]) => (
            <div key={l} className="bg-slate-50 rounded-xl px-3 py-3 text-center">
              <p className="text-xs text-slate-400 mb-1">{l}</p>
              <p className="text-sm font-bold text-slate-900">{v}</p>
              <p className="text-xs text-slate-400 italic">{s}</p>
            </div>
          ))}
        </div>
      )}

      {/* PoA-specific aggregation flow */}
      {(pathway.status === "viable") && (
        <div className="bg-[#0F172A] rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Aggregation Flow</p>
          <div className="flex items-center gap-2">
            {[
              { label: "Demo unit", sub: `1 unit · ${demo.embodied_reduction_tco2e}t embodied`, color: "border-orange-200 bg-orange-50" },
              { label: "Batch",     sub: `100 units · ${Math.round((demo.embodied_reduction_tco2e||0)*100)}t`, color: "border-amber-200 bg-amber-50" },
              { label: "Programme", sub: `${(prog.units_count||1000).toLocaleString()} units · ${(prog.total_embodied_tco2e||0).toLocaleString()}t`, color: "border-teal-200 bg-teal-50" },
            ].map((step, i) => (
              <React.Fragment key={step.label}>
                <div className={`flex-1 rounded-xl border-2 p-3 text-center ${step.color}`}>
                  <p className="text-xs font-bold text-slate-900">{step.label}</p>
                  <p className="text-xs text-slate-500">{step.sub}</p>
                </div>
                {i < 2 && <ArrowRight size={12} className="text-slate-400 flex-shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Next action */}
      <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
        <ArrowRight size={12} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs text-blue-500 font-bold uppercase tracking-wide mb-0.5">Next Action</p>
          <p className="text-xs text-blue-700 leading-snug">{pathway.next_action}</p>
        </div>
      </div>
    </div>
  );
}

// ── Credit issuance estimate (from journey data — not local calc) ──────────
function CreditIssuanceEstimate({ journey }) {
  const demo = journey?.demo_unit || {};
  const prog = journey?.programme || {};
  const N = prog.units_count || 1000;
  const CREDITING_YEARS  = 7;
  const BUFFER_PCT       = 0.15;
  const LEAKAGE_PCT      = 0.05;
  const embodied_total   = Math.round((demo.embodied_reduction_tco2e || 0) * N);
  const op_per_yr        = Math.round((demo.operational_reduction_tco2e_yr || 0) * N);
  const op_total_period  = op_per_yr * CREDITING_YEARS;
  const gross_total      = embodied_total + op_total_period;
  const leakage          = Math.round(gross_total * LEAKAGE_PCT);
  const buffer           = Math.round((gross_total - leakage) * BUFFER_PCT);
  const net_issuable     = gross_total - leakage - buffer;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="credit-issuance">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Credit Issuance Estimate</p>
        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
          {CREDITING_YEARS}yr · Subject to validation
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        {[
          ["Gross total", `${gross_total.toLocaleString()} tCO₂e`, "text-slate-900"],
          ["Net issuable", `${net_issuable.toLocaleString()} tCO₂e`, "text-teal-700"],
        ].map(([l, v, c]) => (
          <div key={l} className="bg-slate-50 rounded-xl px-3 py-2.5 text-center">
            <p className="text-xs text-slate-400 mb-0.5">{l}</p>
            <p className={`text-lg font-bold ${c}`}>{v}</p>
          </div>
        ))}
      </div>
      {[
        ["Embodied credits", `${embodied_total.toLocaleString()} tCO₂e`, "text-orange-600", "Construction phase"],
        ["Annual operational", `${Math.round(op_per_yr*(1-LEAKAGE_PCT)*(1-BUFFER_PCT)).toLocaleString()} tCO₂e/yr`, "text-blue-600", "After buffer/leakage"],
        ["Leakage (5%)", `-${leakage.toLocaleString()} tCO₂e`, "text-red-500", "Conservative"],
        ["Buffer (15%)", `-${buffer.toLocaleString()} tCO₂e`, "text-amber-600", "Permanence risk"],
      ].map(([l, v, c, sub]) => (
        <div key={l} className="flex items-start justify-between py-1.5 border-b border-slate-50 last:border-0">
          <div>
            <p className="text-xs font-semibold text-slate-700">{l}</p>
            <p className="text-xs text-slate-400">{sub}</p>
          </div>
          <span className={`text-xs font-bold flex-shrink-0 ml-3 ${c}`}>{v}</span>
        </div>
      ))}
      <p className="text-xs text-slate-400 italic mt-2 pt-2 border-t border-slate-100">
        Indicative estimates only. Actual credits subject to methodology selection, verification, and registry approval.
      </p>
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export default function HousingCarbonStrategy() {
  const navigate = useNavigate();
  const [active, setActive] = useState("insetting");
  const { journey, loading, pathways, carbonPathway, org, proj } = useHousingJourney();

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  // ── Pathway definitions — status driven entirely from backend ─────────────
  const PATHWAY_TABS = [
    { id: "insetting", name: "Insetting",     status: pathways.insetting?.status || "not_eligible" },
    { id: "poa",       name: "Programmatic",  status: pathways.poa?.status       || "not_eligible" },
    { id: "vcm",       name: "VCM",           status: pathways.vcm?.status       || "not_eligible" },
    { id: "article6",  name: "Article 6",     status: pathways.article6?.status  || "not_eligible" },
  ];

  const activePathway = pathways[active] || null;

  // Overall readiness: at least one pathway is recommended or viable
  const overallReady = Object.values(pathways).some(
    p => ["recommended","high","viable"].includes(p.status)
  );

  return (
    <div className="p-6 lg:p-8 min-h-screen">

        <button onClick={() => navigate("/housing-project")}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> Housing Project
        </button>

        <div className="mb-6">
          
      <PageHeader
        title="Carbon Strategy"
        backUrl="/housing-project"
        backLabel="Housing Project"
      />
          <p className="text-xs text-slate-400 mt-0.5">
            Value pathways and carbon market eligibility — data from{" "}
            <span className="font-semibold">{journey?.version?.label || "latest calculation"}</span>
          </p>
        </div>

        {/* Pathway selector — status from backend only */}
        <div className="flex flex-wrap gap-2 mb-6 p-4 bg-white border border-slate-200 rounded-xl"
          data-testid="pathway-strip">
          {PATHWAY_TABS.map(p => (
            <PathwayPill key={p.id} name={p.name} status={p.status}
              active={active === p.id} onClick={() => setActive(p.id)} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Left 2/3 — pathway detail */}
          <div className="lg:col-span-2">
            <PathwayDetailBlock pathway={activePathway} journey={journey} />
          </div>

          {/* Right 1/3 — eligibility summary */}
          <div className="space-y-4">

            {/* Pathway summary — backend-driven statuses */}
            <div className="bg-white border border-slate-200 rounded-xl p-5"
              data-testid="eligibility-summary">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
                Pathway Summary
              </p>
              {PATHWAY_TABS.map(p => {
                const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.low;
                return (
                  <div key={p.id}
                    className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <p className="text-xs font-semibold text-slate-900">{p.name}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                      {cfg.label}
                    </span>
                  </div>
                );
              })}
              <p className="text-xs text-slate-400 italic mt-2 pt-2 border-t border-slate-100">
                {carbonPathway?.methodology_note}
              </p>
            </div>

            {/* Eligibility outcome */}
            <div className={`border-2 rounded-xl p-5 ${overallReady ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}
              data-testid="eligibility-outcome">
              <div className="flex items-center gap-2 mb-2">
                {overallReady
                  ? <CheckCircle2 size={16} className="text-emerald-600" />
                  : <AlertCircle  size={16} className="text-amber-600" />}
                <p className={`text-sm font-bold ${overallReady ? "text-emerald-800" : "text-amber-800"}`}>
                  {overallReady ? "Ready to Structure" : "Conditions Required"}
                </p>
              </div>
              <p className={`text-xs mb-4 leading-relaxed ${overallReady ? "text-emerald-600" : "text-amber-600"}`}>
                {overallReady
                  ? "One or more viable pathways exist. Proceed to carbon structuring workspace."
                  : "Meet pathway requirements before proceeding to structuring."}
              </p>
              <button onClick={() => navigate("/housing/carbon-structuring")}
                className={`w-full flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl ${
                  overallReady ? "bg-[#0F172A] text-white hover:bg-slate-800" : "bg-amber-600 text-white hover:bg-amber-700"
                }`}
                data-testid="proceed-to-structuring">
                {overallReady ? "Proceed to Carbon Structuring" : "Review Requirements"} <ArrowRight size={14} />
              </button>
            </div>

            {/* Article 6 readiness note */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">
                Article 6 Readiness
              </p>
              <p className="text-xs text-slate-500 leading-relaxed">
                Not currently eligible. Article 6 Readiness Pack (template) available in{" "}
                <button onClick={() => navigate("/housing/reports")}
                  className="text-teal-600 underline">Reports</button>.
              </p>
            </div>

            {/* Credit issuance estimate */}
            <CreditIssuanceEstimate journey={journey} />

          </div>
        </div>

      <p className="text-xs text-slate-400 italic text-center mt-5 pb-2">
        This platform supports preparation and structuring of carbon projects.
        Final issuance requires third-party validation and registry approval.
      </p>
    </div>
  );
}
